/**
 * @fileoverview HypayFailurePage - עמוד כישלון לתשלומי כספית
 * @description עמוד שמוצג כאשר תשלום דרך כספית נכשל
 */
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  XCircle, 
  AlertTriangle, 
  Home, 
  RotateCcw,
  CreditCard,
  Phone,
  Mail,
  HelpCircle
} from "lucide-react";

const HypayFailurePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // קבלת פרמטרים מכספית
  const transactionId = searchParams.get('Id');
  const ccode = searchParams.get('CCode');
  const amount = searchParams.get('Amount');
  const orderId = searchParams.get('Order');
  const errorMessage = searchParams.get('errMsg');

  /**
   * 📊 מיפוי קודי שגיאה נפוצים של כספית
   */
  const getErrorDetails = (code) => {
    const errorCodes = {
      '1': { title: 'כרטיס חסום', description: 'הכרטיס חסום על ידי חברת האשראי' },
      '2': { title: 'כרטיס גנוב/חרם', description: 'הכרטיס דווח כגנוב או חרם' },
      '3': { title: 'פנה לחברת האשראי', description: 'יש ליצור קשר עם חברת האשראי' },
      '4': { title: 'עסקה לא אושרה', description: 'העסקה נדחתה על ידי חברת האשראי' },
      '5': { title: 'כרטיס מזוייף', description: 'הכרטיס מזוייף - החרם הכרטיס' },
      '6': { title: 'CVV שגוי', description: 'קוד האבטחה (CVV) שהוזן שגוי' },
      '15': { title: 'כרטיס לא בתוקף', description: 'תוקף הכרטיס פג או עדיין לא בתוקף' },
      '26': { title: 'מספר זהות שגוי', description: 'מספר הזהות שהוזן אינו תקין' },
      '500': { title: 'בוטל על ידי המשתמש', description: 'העסקה בוטלה על ידי המשתמש' },
      '902': { title: 'שגיאת אימות', description: 'שגיאה באימות פרטי המסוף' },
      '999': { title: 'שגיאת תקשורת', description: 'שגיאה בתקשורת עם חברת האשראי' }
    };

    return errorCodes[code] || { 
      title: 'שגיאה לא ידועה', 
      description: `קוד שגיאה: ${code}` 
    };
  };

  const errorDetails = getErrorDetails(ccode);

  /**
   * 🔄 ניסיון נוסף לתשלום
   */
  const retryPayment = () => {
    navigate('/cart');
  };

  /**
   * 🏠 חזרה לעמוד הבית
   */
  const goHome = () => {
    navigate('/');
  };

  /**
   * 📞 צור קשר עם תמיכה
   */
  const contactSupport = () => {
    const emailBody = `שלום,

קיבלתי שגיאה בתשלום דרך כספית:

פרטי השגיאה:
- מספר עסקה: ${transactionId || 'לא זמין'}
- קוד שגיאה: ${ccode}
- מספר הזמנה: ${orderId || 'לא זמין'}
- סכום: ₪${amount || 'לא זמין'}
- הודעת שגיאה: ${errorMessage || 'לא זמינה'}
- זמן: ${new Date().toLocaleString('he-IL')}

אנא עזרו לי לפתור את הבעיה.

תודה,
`;

    const mailtoLink = `mailto:support@sha-sha.co.il?subject=שגיאה בתשלום דרך כספית - ${ccode}&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* הודעת שגיאה ראשית */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <XCircle className="w-20 h-20 text-red-600 mx-auto mb-4" />
              </motion.div>
              
              <h1 className="text-3xl font-bold text-red-900 mb-2">
                התשלום לא הושלם
              </h1>
              
              <p className="text-red-700 text-lg mb-6">
                מצטערים, אירעה שגיאה בעת ביצוע התשלום
              </p>
            </div>

            {/* פרטי השגיאה */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-900 mb-2">
                    {errorDetails.title}
                  </h3>
                  <p className="text-red-700 mb-4">
                    {errorDetails.description}
                  </p>
                  
                  {errorMessage && (
                    <div className="bg-red-100 rounded p-3">
                      <p className="text-red-800 text-sm">
                        <strong>הודעת מערכת:</strong> {decodeURIComponent(errorMessage)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* פרטי העסקה */}
            {(transactionId || orderId || amount) && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  פרטי העסקה
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {transactionId && (
                    <div>
                      <span className="text-sm text-gray-600">מספר עסקה</span>
                      <p className="font-semibold text-gray-900">{transactionId}</p>
                    </div>
                  )}
                  
                  {orderId && (
                    <div>
                      <span className="text-sm text-gray-600">מספר הזמנה</span>
                      <p className="font-semibold text-gray-900">{orderId}</p>
                    </div>
                  )}
                  
                  {amount && (
                    <div>
                      <span className="text-sm text-gray-600">סכום</span>
                      <p className="font-semibold text-gray-900">₪{parseFloat(amount).toFixed(2)}</p>
                    </div>
                  )}
                  
                  <div>
                    <span className="text-sm text-gray-600">קוד שגיאה</span>
                    <p className="font-semibold text-red-600">{ccode}</p>
                  </div>
                  
                  <div>
                    <span className="text-sm text-gray-600">תאריך ושעה</span>
                    <p className="font-semibold text-gray-900">
                      {new Date().toLocaleDateString('he-IL')} {new Date().toLocaleTimeString('he-IL')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* פעולות */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={retryPayment}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold
                         hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                         flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <RotateCcw className="w-5 h-5" />
                <span>נסה שוב</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={goHome}
                className="flex-1 bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold
                         hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                         flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <Home className="w-5 h-5" />
                <span>חזור לעמוד הבית</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={contactSupport}
                className="flex-1 bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold
                         hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
                         flex items-center justify-center space-x-2 rtl:space-x-reverse"
              >
                <Mail className="w-5 h-5" />
                <span>צור קשר</span>
              </motion.button>
            </div>
          </div>

          {/* הדרכה לפתרון בעיות */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <HelpCircle className="w-5 h-5 text-blue-600 ml-2" />
              פתרונות מומלצים
            </h3>
            
            <div className="space-y-4">
              {ccode === '6' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">קוד CVV שגוי</h4>
                  <p className="text-blue-700 text-sm">
                    • ודא שהזנת את קוד האבטחה הנכון (3 ספרות בגב הכרטיס)
                    <br />• בכרטיסי אמריקן אקספרס הקוד מופיע בחזית הכרטיס (4 ספרות)
                  </p>
                </div>
              )}

              {ccode === '15' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">כרטיס לא בתוקף</h4>
                  <p className="text-yellow-700 text-sm">
                    • בדוק את תאריך התוקף של הכרטיס
                    <br />• ודא שהכרטיס עדיין תקף
                    <br />• נסה עם כרטיס אחר
                  </p>
                </div>
              )}

              {ccode === '26' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-900 mb-2">מספר זהות שגוי</h4>
                  <p className="text-orange-700 text-sm">
                    • ודא שהזנת את מספר הזהות הנכון
                    <br />• מספר הזהות חייב להתאים לבעל הכרטיס
                  </p>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">פתרונות כלליים</h4>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• נסה שוב עם כרטיס אשראי אחר</li>
                  <li>• ודא שיש לך מספיק זכות אשראי</li>
                  <li>• בדוק עם הבנק שלך שהכרטיס פעיל לקניות אינטרנט</li>
                  <li>• נסה מדפדפן אחר או מחשב אחר</li>
                </ul>
              </div>
            </div>
          </div>

          {/* מידע יצירת קשר */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              זקוק לעזרה נוספת?
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">טלפון</h4>
                  <p className="text-gray-600 text-sm">053-33321277</p>
                  <p className="text-gray-500 text-xs">ימים א-ה 9:00-18:00</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">אימייל</h4>
                  <p className="text-gray-600 text-sm">nataly@sha-sha.co.il</p>
                  <p className="text-gray-500 text-xs">מענה תוך 24 שעות</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HypayFailurePage; 